import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  BatteryChargingFull,
  Bolt,
  Business,
  Cloud,
  Memory,
  Pause,
  PlayArrow,
  SolarPower,
} from '@mui/icons-material';
import { useAppContext } from '../../context/AppContext';
import { useViewContext } from '../../context/ViewContext';
import { formatValue, signedPowerLabel } from '../../utils/energyViewModel';
import { EnergyStatusChip } from './EnergyUi';

const COLORS = {
  canopy: '#f57c00',
  bess: '#388e3c',
  ems: '#00897b',
  load: '#7b1fa2',
  pcc: '#1976d2',
};

const AssetNode = ({ icon, title, value, state, color, onClick, ariaLabel, compact = false }) => (
  <Paper
    component={onClick ? 'button' : 'div'}
    type={onClick ? 'button' : undefined}
    onClick={onClick}
    variant="outlined"
    aria-label={ariaLabel || title}
    sx={{
      p: compact ? 1 : 1.25,
      width: '100%',
      minHeight: compact ? 72 : 94,
      textAlign: 'left',
      bgcolor: 'background.paper',
      borderTop: 3,
      borderTopColor: color,
      borderRadius: 1,
      cursor: onClick ? 'pointer' : 'default',
      font: 'inherit',
      transition: 'box-shadow 120ms ease, transform 120ms ease, background-color 120ms ease',
      '&:hover': onClick ? { bgcolor: 'action.hover', boxShadow: 2, transform: 'translateY(-1px)' } : undefined,
      '&:focus-visible': { outline: '3px solid', outlineColor: 'primary.main', outlineOffset: 2 },
    }}
  >
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box sx={{ color, display: 'flex' }}>{icon}</Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" fontWeight={700}>{title}</Typography>
        {value && <Typography variant={compact ? 'body2' : 'subtitle1'} fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>}
      </Box>
    </Stack>
    <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ mt: 0.25 }}>
      {state || '-'}
    </Typography>
  </Paper>
);

const FLOW_KEYFRAMES = {
  right: {
    name: 'energyFlowRight',
    frames: { from: { transform: 'translateX(-100%)' }, to: { transform: 'translateX(300%)' } },
  },
  left: {
    name: 'energyFlowLeft',
    frames: { from: { transform: 'translateX(300%)' }, to: { transform: 'translateX(-100%)' } },
  },
  down: {
    name: 'energyFlowDown',
    frames: { from: { transform: 'translateY(-100%)' }, to: { transform: 'translateY(300%)' } },
  },
  up: {
    name: 'energyFlowUp',
    frames: { from: { transform: 'translateY(300%)' }, to: { transform: 'translateY(-100%)' } },
  },
};

const FlowLine = ({ color, active, animate, vertical = false, direction, flowId }) => {
  const resolvedDirection = direction || (vertical ? 'down' : 'right');
  const keyframes = FLOW_KEYFRAMES[resolvedDirection];
  return (
    <Box
      aria-hidden="true"
      data-flow-id={flowId}
      data-flow-direction={resolvedDirection}
      data-flow-active={active ? 'true' : 'false'}
      sx={{
        position: 'relative',
        width: vertical ? 4 : '100%',
        height: vertical ? '100%' : 4,
        minHeight: vertical ? 20 : undefined,
        minWidth: vertical ? undefined : 20,
        borderRadius: 3,
        bgcolor: `${color}2b`,
        overflow: 'hidden',
        '&::after': active ? (animate ? {
          content: '""',
          position: 'absolute',
          width: vertical ? '100%' : '36%',
          height: vertical ? '36%' : '100%',
          bgcolor: color,
          borderRadius: 3,
          animation: `${keyframes.name} 1.5s linear infinite`,
          [`@keyframes ${keyframes.name}`]: keyframes.frames,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none', width: '100%', height: '100%', opacity: 0.65 },
        } : {
          content: '""', position: 'absolute', inset: 0, bgcolor: color, opacity: 0.55,
        }) : { content: '""' },
      }}
    />
  );
};

const finitePower = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const powerIsPositive = (value) => {
  const power = finitePower(value);
  return power !== null && power > 0;
};

const powerDirection = (value, positiveDirection, negativeDirection) => {
  const power = finitePower(value);
  if (power === null || power === 0) return null;
  return power > 0 ? positiveDirection : negativeDirection;
};

const displayState = (state) => !state || String(state).toLowerCase() === 'unavailable' ? '-' : state;

const CanopyNode = ({ canopy, openCanopy }) => (
  <AssetNode
    compact
    icon={<SolarPower fontSize="small" />}
    title={canopy.name}
    value={formatValue(canopy.powerKw, 'kW')}
    state={`${displayState(canopy.state)}${canopy.angleDeg !== null && canopy.angleDeg !== undefined ? ` · ${canopy.angleDeg}°` : ''}`}
    color={COLORS.canopy}
    onClick={() => openCanopy(canopy)}
    ariaLabel={`Open ${canopy.name} telemetry and canopy animation; ${formatValue(canopy.powerKw, 'kilowatts')}; ${displayState(canopy.state)}`}
  />
);

const canopyNumber = (canopy, index) => {
  const match = String(canopy.id || canopy.name || '').match(/(\d+)(?!.*\d)/);
  return match ? Number(match[1]) : index + 1;
};

// Positions follow the supplied logical layout. The scroll surface keeps labels
// readable on small displays while preserving the same physical connections.
const PositionedNode = ({ left, top, width, children }) => (
  <Box sx={{ position: 'absolute', left: `${left}%`, top, width: `${width}%`, zIndex: 2 }}>{children}</Box>
);

const ElectricalConnection = ({ left, top, length, vertical = false, ...props }) => (
  <Box sx={{ position: 'absolute', left: `${left}%`, top, width: vertical ? 4 : `${length}%`, height: vertical ? length : 4, transform: vertical ? 'translateX(-50%)' : undefined }}>
    <FlowLine vertical={vertical} {...props} />
    {props.active && <Box aria-hidden="true" sx={{ position: 'absolute', left: '50%', top: '50%', width: 0, height: 0,
      borderTop: '5px solid transparent', borderBottom: '5px solid transparent', borderLeft: `7px solid ${props.color}`,
      transform: `translate(-50%, -50%) rotate(${{ right: 0, down: 90, left: 180, up: 270 }[props.direction] || 0}deg)` }} />}
  </Box>
);

export default function InstallationEnergyDiagram({ model, onOpenEms }) {
  const { setCanopy } = useAppContext();
  const { setView } = useViewContext();
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [paused, setPaused] = useState(false);
  const animate = !paused && !reducedMotion;
  const canopies = useMemo(() => model.canopies.slice(0, 10), [model.canopies]);
  const canopyPairs = useMemo(() => {
    const ordered = [...canopies].sort((left, right) => (
      canopyNumber(left, canopies.indexOf(left)) - canopyNumber(right, canopies.indexOf(right))
    ));
    const odd = ordered.filter((canopy, index) => canopyNumber(canopy, index) % 2 === 1);
    const even = ordered.filter((canopy, index) => canopyNumber(canopy, index) % 2 === 0);
    return Array.from({ length: Math.max(odd.length, even.length) }, (_, index) => [odd[index], even[index]]);
  }, [canopies]);
  const fresh = !['stale', 'offline', 'unavailable', 'missing', 'invalid'].includes(String(model.meta.quality).toLowerCase());
  const gridPower = model.pcc.powerKw ?? model.power.gridKw;
  const gridDirection = fresh ? powerDirection(gridPower, 'down', 'up') : null;
  const batteryPower = model.bess.netKw ?? model.bess.actualKw ?? model.power.bessKw;
  const batteryDirection = fresh ? powerDirection(batteryPower, 'left', 'right') : null;
  const pcsPower = finitePower(model.bess.pcsKw);
  const batteryState = pcsPower === null ? '-' : pcsPower > 0 ? 'Discharging' : pcsPower < 0 ? 'Charging' : 'Standby';
  const loadActive = fresh && powerIsPositive(model.load.powerKw ?? model.power.loadKw);
  const solarActive = fresh && (powerIsPositive(model.power.solarKw)
    || canopies.some((canopy) => powerIsPositive(canopy.powerKw)));
  const drawingHeight = 455 + Math.max(5, canopyPairs.length) * 88;

  const openCanopy = (canopy) => {
    const selected = setCanopy({ canopyId: canopy.id, canopyName: canopy.name });
    if (selected) setView('canopyView');
  };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, height: '100%' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>Installation &amp; power flow schematic</Typography>
          <Typography variant="caption" color="text.secondary">Select option for more detail.</Typography>
        </Box>
        <Button size="small" variant="outlined" startIcon={animate ? <Pause /> : <PlayArrow />}
          onClick={() => setPaused((value) => !value)} disabled={reducedMotion}>
          {reducedMotion ? 'Reduced motion' : animate ? 'Pause motion' : 'Resume motion'}
        </Button>
      </Stack>

      <Box role="region" aria-label="Electrical system layout" tabIndex={0}
        sx={{ overflowX: 'auto', bgcolor: '#f5f5f5', borderRadius: 1, border: '1px solid', borderColor: 'divider', p: { xs: 1, md: 1.5 }, '&:focus-visible': { outline: '3px solid', outlineColor: 'primary.main' } }}>
        <Box sx={{ position: 'relative', minWidth: 650, height: drawingHeight }}>
          <Box component="svg" viewBox={`0 0 1000 ${drawingHeight}`} preserveAspectRatio="none" aria-hidden="true" data-testid="communication-paths"
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
            <g fill="none" stroke="#757575" strokeWidth="2" strokeDasharray="6 5">
              <path d="M 510 187 H 895 V 285" />
              <path d="M 730 332 H 790" />
              <path d="M 895 379 V 743" />
              <path d={`M 770 455 V ${drawingHeight - 25} M 770 790 H 800`} />
            </g>
          </Box>
          <PositionedNode left={26} top={10} width={25}>
            <AssetNode icon={<Bolt />} title="Grid" value={formatValue(gridPower, 'kW')}
              state={signedPowerLabel(gridPower, 'Importing', 'Exporting')} color={COLORS.pcc} onClick={() => setView('pccView')}
              ariaLabel="Open Grid details and PCC meter readings" />
          </PositionedNode>
          <ElectricalConnection left={38.5} top={104} length={36} vertical color={COLORS.pcc} active={Boolean(gridDirection)} animate={animate}
            direction={gridDirection || 'down'} flowId="grid-to-switchgear" />
          <PositionedNode left={0} top={140} width={23}>
            <AssetNode icon={<Business />} title="Customer load" value={formatValue(model.load.powerKw ?? model.power.loadKw, 'kW')}
              state={displayState(model.load.profile)} color={COLORS.load} onClick={() => setView('loadView')} />
          </PositionedNode>
          <ElectricalConnection left={23} top={187} length={3} color={COLORS.load} active={loadActive} animate={animate} direction="left" flowId="bus-to-load" />
          <PositionedNode left={26} top={140} width={25}>
            <AssetNode icon={<Memory />} title="Switchgear" state="Electrical interconnection" color={COLORS.pcc} />
          </PositionedNode>
          <ElectricalConnection left={38.5} top={234} length={drawingHeight - 275} vertical color={COLORS.canopy}
            active={solarActive} animate={animate} direction="up" flowId="solar-bus-to-site-bus" />
          <ElectricalConnection left={44} top={234} length={98} vertical color={COLORS.bess}
            active={Boolean(batteryDirection)} animate={animate} direction={batteryDirection === 'left' ? 'up' : 'down'} flowId="switchgear-and-battery-vertical" />
          <ElectricalConnection left={44} top={332} length={4} color={COLORS.bess} active={Boolean(batteryDirection)}
            animate={animate} direction={batteryDirection || 'right'} flowId="bus-and-battery" />
          <PositionedNode left={48} top={285} width={25}>
            <AssetNode icon={<BatteryChargingFull />} title="Battery storage" value={formatValue(batteryPower, 'kW')}
              state={`${batteryState} · SOC ${formatValue(model.bess.socPct, '%')}`} color={COLORS.bess} onClick={() => setView('bessView')}
              ariaLabel={`Open Battery storage; ${batteryState}; net ${formatValue(batteryPower, 'kilowatts')}; auxiliary demand ${formatValue(model.bess.auxiliaryKw, 'kilowatts')}`} />
          </PositionedNode>
          <PositionedNode left={79} top={285} width={21}>
            <AssetNode icon={<Memory />} title="EMS controller" value={displayState(model.ems.mode)} state={displayState(model.ems.objective)}
              color={COLORS.ems} onClick={onOpenEms || (() => setView('emsView'))} ariaLabel="Open EMS dispatch logic and constraints" />
          </PositionedNode>

          {canopyPairs.map(([left, right], index) => {
            const top = 455 + index * 88;
            return (
              <React.Fragment key={`${left?.id || 'left'}-${right?.id || 'right'}-${index}`}>
                <PositionedNode left={0} top={top} width={34}>{left && <CanopyNode canopy={left} openCanopy={openCanopy} />}</PositionedNode>
                <ElectricalConnection left={34} top={top + 36} length={4.5} color={COLORS.canopy}
                  active={fresh && powerIsPositive(left?.powerKw) && !['stale', 'offline', 'unavailable', 'invalid'].includes(left?.quality)}
                  animate={animate} direction="right" flowId={`canopy-left-${left?.id || index}`} />
                <ElectricalConnection left={38.5} top={top + 36} length={4.5} color={COLORS.canopy}
                  active={fresh && powerIsPositive(right?.powerKw) && !['stale', 'offline', 'unavailable', 'invalid'].includes(right?.quality)}
                  animate={animate} direction="left" flowId={`canopy-right-${right?.id || index}`} />
                <PositionedNode left={43} top={top} width={32}>{right && <CanopyNode canopy={right} openCanopy={openCanopy} />}</PositionedNode>
              </React.Fragment>
            );
          })}
          <PositionedNode left={80} top={743} width={20}>
            <AssetNode icon={<Cloud />} title="Sabal Cloud" state="via EMQX" color="#607d8b" />
          </PositionedNode>
        </Box>
      </Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
        <Chip size="small" icon={<SolarPower />} label="Solar" sx={{ color: COLORS.canopy }} />
        <Chip size="small" icon={<BatteryChargingFull />} label="Battery" sx={{ color: COLORS.bess }} />
        <Chip size="small" icon={<Business />} label="Load" sx={{ color: COLORS.load }} />
        <Chip size="small" icon={<Bolt />} label="Grid" sx={{ color: COLORS.pcc }} />
        <Chip size="small" variant="outlined" label="Dashed lines: telemetry and control" />
        <EnergyStatusChip status={model.meta.quality} />
      </Stack>
    </Paper>
  );
}
